// Apolo IDE — useDecomposer hook (Sprint v4 Epic S2 WS S2.3).
//
// Owns the local state of a Decompose-Mode session:
//   - plan          : last plan returned by /api/agents/decompose
//   - planId        : server-issued id (same as dispatchId once approved)
//   - busy          : true while decomposing OR approving
//   - phase         : 'idle' | 'planning' | 'awaiting-approval' | 'approving'
//                     | 'approved' | 'rejected' | 'error'
//   - error         : last failure message
//   - budget        : warning + spend snapshot returned by the API
//
// Plus actions: decompose, approve, reject, reset.
//
// The hook does not own the network layer — `deps.decompose|approve|reject`
// can be stubbed by tests. Default uses apiFetch against /api/agents/*.

import { useCallback, useState } from 'react';
import { apiFetch } from '../lib/api.js';

async function defaultDecompose(payload) {
  return apiFetch('/api/agents/decompose', { method: 'POST', body: payload });
}

async function defaultApprove(planId) {
  return apiFetch(`/api/agents/plan/${encodeURIComponent(planId)}/approve`, {
    method: 'POST',
    body: {},
  });
}

async function defaultReject(planId) {
  return apiFetch(`/api/agents/plan/${encodeURIComponent(planId)}/reject`, {
    method: 'POST',
    body: {},
  });
}

export default function useDecomposer(options = {}) {
  const decomposeFn = options.decompose || defaultDecompose;
  const approveFn = options.approve || defaultApprove;
  const rejectFn = options.reject || defaultReject;

  const [plan, setPlan] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState(null);
  const [budget, setBudget] = useState(null);
  const [approval, setApproval] = useState(null);

  const reset = useCallback(() => {
    setPlan(null);
    setPlanId(null);
    setError(null);
    setBudget(null);
    setApproval(null);
    setPhase('idle');
  }, []);

  const decompose = useCallback(
    async ({ message, sessionId, context, availableTools, maxSubagents, maxMcpAccess }) => {
      if (typeof message !== 'string' || message.trim().length < 10) {
        const m = 'Message must be at least 10 characters';
        setError(m);
        setPhase('error');
        return { ok: false, error: m };
      }
      setBusy(true);
      setError(null);
      setPlan(null);
      setPlanId(null);
      setApproval(null);
      setPhase('planning');
      try {
        const res = await decomposeFn({
          message: message.trim(),
          sessionId,
          context,
          availableTools,
          maxSubagents,
          maxMcpAccess,
        });
        if (!res.ok) {
          const m = (res.data && res.data.error) || res.error || 'Decompose failed';
          setError(m);
          setPhase('error');
          return { ok: false, error: m, status: res.status };
        }
        const data = res.data || res;
        setPlan(data.plan || null);
        setPlanId(data.planId || data.plan?.planId || null);
        setBudget(data.budget || null);
        setPhase('awaiting-approval');
        return { ok: true, data };
      } catch (err) {
        const m = err && err.message ? err.message : String(err);
        setError(m);
        setPhase('error');
        return { ok: false, error: m };
      } finally {
        setBusy(false);
      }
    },
    [decomposeFn],
  );

  const approve = useCallback(async () => {
    if (!planId) {
      const m = 'No plan to approve';
      setError(m);
      return { ok: false, error: m };
    }
    setBusy(true);
    setError(null);
    setPhase('approving');
    try {
      const res = await approveFn(planId);
      if (!res.ok) {
        const m = (res.data && res.data.error) || res.error || 'Approve failed';
        setError(m);
        setPhase('awaiting-approval'); // stay on plan so user can re-try
        return { ok: false, error: m, status: res.status };
      }
      const data = res.data || res;
      setApproval(data);
      setPhase('approved');
      return { ok: true, data };
    } catch (err) {
      const m = err && err.message ? err.message : String(err);
      setError(m);
      setPhase('awaiting-approval');
      return { ok: false, error: m };
    } finally {
      setBusy(false);
    }
  }, [approveFn, planId]);

  const reject = useCallback(async () => {
    if (!planId) {
      const m = 'No plan to reject';
      setError(m);
      return { ok: false, error: m };
    }
    setBusy(true);
    setError(null);
    try {
      const res = await rejectFn(planId);
      if (!res.ok) {
        const m = (res.data && res.data.error) || res.error || 'Reject failed';
        setError(m);
        return { ok: false, error: m };
      }
      setPhase('rejected');
      return { ok: true, data: res.data || res };
    } catch (err) {
      const m = err && err.message ? err.message : String(err);
      setError(m);
      return { ok: false, error: m };
    } finally {
      setBusy(false);
    }
  }, [rejectFn, planId]);

  return {
    // state
    plan,
    planId,
    busy,
    phase,
    error,
    budget,
    approval,
    // actions
    decompose,
    approve,
    reject,
    reset,
  };
}
